import React, { useEffect, useState, useCallback } from 'react';
import { featureAPI } from '../../../api/services';
import { Feature, FeatureStatus, FeatureApprovalStatus } from '../../../types';

const STATUS_COLORS: Record<FeatureStatus, string> = {
  planning: 'bg-gray-100 text-gray-700',
  development: 'bg-blue-100 text-blue-700',
  testing: 'bg-purple-100 text-purple-700',
  done: 'bg-green-100 text-green-700',
  deployed: 'bg-teal-100 text-teal-700',
  archived: 'bg-gray-200 text-gray-400',
};

const APPROVAL_COLORS: Record<FeatureApprovalStatus, string> = {
  not_required: '',
  pending_approval: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  disputed: 'bg-orange-100 text-orange-700',
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-gray-100 text-gray-600',
};

interface Props {
  projectId: string;
  onSelectFeature?: (featureId: string) => void;
  onNewFeature?: () => void;
}

const FeatureList: React.FC<Props> = ({ projectId, onSelectFeature, onNewFeature }) => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const fetchFeatures = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await featureAPI.getByProject(projectId);
      const payload = res.data.data ?? res.data;
      setFeatures(Array.isArray(payload) ? payload : []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load features');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  const filtered = features.filter(f => {
    if (statusFilter && f.status !== statusFilter) return false;
    if (search && !f.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}

      {/* Filters & search -- only shown when features exist */}
      {features.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search features..."
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1 min-w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
          >
            <option value="">All Status</option>
            {(['planning', 'development', 'testing', 'done', 'deployed', 'archived'] as FeatureStatus[]).map(s => (
              <option key={s} value={s} className="capitalize">{s}</option>
            ))}
          </select>
          <button
            onClick={onNewFeature}
            className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 ml-auto"
          >
            + New Feature
          </button>
        </div>
      )}

      {/* Feature list */}
      {loading && features.length === 0 ? (
        <div className="flex justify-center p-8"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>
      ) : features.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-8">
          <div className="w-20 h-20 rounded-2xl bg-blue-50 flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875S10.5 3.09 10.5 4.125c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 01-.657.643 48.421 48.421 0 01-4.185-.408c-.009-.006-.018-.013-.028-.018a.556.556 0 01-.315-.506v-.003c0-.264.105-.501.272-.694a.938.938 0 00.241-.632c0-.553-.494-1.012-1.1-1.012-.607 0-1.1.46-1.1 1.012 0 .233.1.448.241.632.167.193.272.43.272.694v.003a.556.556 0 01-.315.506c-.01.005-.019.012-.028.018a48.36 48.36 0 01-4.185.408.64.64 0 01-.657-.643v0c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875S.75 3.09.75 4.125c0 .369.128.713.349 1.003.215.283.401.604.401.959M14.25 6.087c0 .355-.186.676-.401.959a1.647 1.647 0 00-.349 1.003c0 1.035 1.007 1.875 2.25 1.875s2.25-.84 2.25-1.875c0-.369-.128-.713-.349-1.003a1.647 1.647 0 01-.401-.959m-9.5 0c0 .355.186.676.401.959.221.29.349.634.349 1.003 0 1.035-1.007 1.875-2.25 1.875S.75 9.085.75 8.049c0-.369.128-.713.349-1.003.215-.283.401-.604.401-.959" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No features yet</h2>
          <p className="text-sm text-gray-500 max-w-sm mb-8">
            Features group related tasks into deliverable units. Create your first feature to organize your work.
          </p>
          <button
            onClick={onNewFeature}
            className="bg-blue-600 text-white px-6 py-3 rounded-xl text-sm font-medium hover:bg-blue-700 shadow-sm transition-colors"
          >
            Create Feature
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-sm font-medium">No results for this filter.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {filtered.map((feature: Feature) => (
            <div
              key={feature._id}
              onClick={() => onSelectFeature?.(feature._id)}
              className="bg-white border border-gray-200 rounded-lg px-4 py-3 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all"
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[feature.status]}`}>
                      {feature.status}
                    </span>
                    {feature.approvalStatus !== 'not_required' && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${APPROVAL_COLORS[feature.approvalStatus]}`}>
                        {feature.approvalStatus.replace('_', ' ')}
                      </span>
                    )}
                    <span className={`text-xs px-1.5 py-0.5 rounded ${PRIORITY_COLORS[feature.priority]}`}>
                      {feature.priority}
                    </span>
                    <span className="text-xs text-gray-400">W: {feature.weight}</span>
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 truncate" title={`${feature.name}${feature.description ? '\n' + feature.description.slice(0, 200) : ''}`}>{feature.name}</h3>
                  <div className="flex gap-3 mt-1 text-xs text-gray-400">
                    <span>{feature.stats.completedTasks}/{feature.stats.totalTasks} tasks</span>
                    {feature.owner?.name && (
                      <>
                        <span>·</span>
                        <span>{feature.owner.name}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeatureList;
